"use client";

import React from "react";
import { SiGooglechrome } from "react-icons/si";
import {
  Download,
  Shield,
  Zap,
  Lock,
  CheckCircle,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const ExtentionInfo = () => {
  const navigate = useNavigate();
  const extensionUrl =
    "https://chromewebstore.google.com/detail/peer-authenticate-and-use/ijpgccednehjpeclfcllnjjcmiohdjih";

  const features = [
    {
      icon: <Zap className="w-6 h-6 sm:w-7 sm:h-7 text-green-400" />,
      title: "Fast P2P Payments",
      description:
        "Instant transfers using Cash App, Venmo, Wise, Revolut and more — directly to your wallet.",
    },
    {
      icon: <Shield className="w-6 h-6 sm:w-7 sm:h-7 text-blue-400" />,
      title: "Secure & Private",
      description:
        "Zero-knowledge proofs keep your transactions private yet verifiable on-chain.",
    },
    {
      icon: <Lock className="w-6 h-6 sm:w-7 sm:h-7 text-purple-400" />,
      title: "No KYC Needed",
      description:
        "Peer-to-peer without centralized identity checks in most cases.",
    },
    {
      icon: <RefreshCw className="w-6 h-6 sm:w-7 sm:h-7 text-cyan-400" />,
      title: "Auto Balance Refresh",
      description:
        "Wallet balance updates automatically after successful payments.",
    },
  ];

  const steps = [
    "Click the big green button below",
    "Click 'Add to Chrome' in the Chrome Web Store",
    "Confirm installation when prompted",
    "Return here and refresh the page — you're ready!",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0e17] via-[#0c1320] to-[#0f1622] text-white pb-16">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 pt-10 pb-12 md:pt-16 md:pb-20">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-green-500/25 to-emerald-600/15 mb-6 border border-green-500/30 shadow-lg">
            <SiGooglechrome className="w-8 h-8 sm:w-10 sm:h-10 text-green-400" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent leading-tight">
            Cashtap Chrome Extension
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Enable fast, private peer-to-peer USD → USDC payments using Cash
            App, Venmo, Wise and more — directly in your browser.
          </p>
        </div>

        {/* Main CTA Card */}
        <div className="bg-gradient-to-br from-[#1a2338] to-[#121a2b] rounded-3xl p-6 sm:p-8 md:p-10 border border-gray-700/50 shadow-2xl mb-12 md:mb-16">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            <div className="flex-1 text-center lg:text-left">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 md:mb-5">
                Install Cashtap Extension
              </h2>
              <p className="text-gray-300 mb-6 sm:mb-8 text-base sm:text-lg leading-relaxed">
                This secure browser extension is required to connect Cash App
                (and other P2P apps) directly to your on-chain wallet — safely
                and privately.
              </p>

              <a
                href={extensionUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 w-full sm:w-auto bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white px-8 sm:px-10 py-4 sm:py-5 rounded-2xl font-semibold text-lg sm:text-xl transition-all shadow-xl shadow-green-900/40 hover:shadow-green-800/50 transform hover:-translate-y-1 active:translate-y-0"
              >
                <Download size={22} className="sm:size-24" />
                Install Cashtap Extension
              </a>

              <p className="mt-5 text-sm sm:text-base text-gray-400">
                Only works in <strong>Google Chrome</strong> on desktop
              </p>
            </div>

            {/* Preview card - hidden on very small screens, shown centered on mobile */}
            <div className="hidden sm:flex w-full sm:w-80 lg:w-96 aspect-video bg-black/50 rounded-2xl border border-gray-700/70 overflow-hidden items-center justify-center mt-8 lg:mt-0">
              <div className="text-center p-6 sm:p-10">
                <SiGooglechrome className="w-16 h-16 sm:w-20 sm:h-20 text-green-400 mx-auto mb-4 opacity-80" />
                <p className="text-gray-400 text-sm sm:text-base">
                  Chrome Web Store
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 md:gap-8 mb-14 md:mb-20">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-[#1a2338]/70 to-[#121a2b]/70 rounded-2xl p-5 sm:p-6 border border-gray-800/60 hover:border-gray-600/60 transition-all duration-300 hover:shadow-xl hover:shadow-black/30"
            >
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-400 text-sm sm:text-base">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Steps */}
        <div className="bg-[#0f1622] rounded-3xl p-6 sm:p-8 md:p-10 border border-gray-800/50 mb-14 md:mb-20">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-center">
            Install in 4 Simple Steps
          </h2>

          <div className="space-y-5 sm:space-y-6 max-w-3xl mx-auto">
            {steps.map((step, index) => (
              <div key={index} className="flex gap-4 sm:gap-5 items-start">
                <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center text-green-400 font-bold text-lg sm:text-xl">
                  {index + 1}
                </div>
                <p className="text-base sm:text-lg text-gray-200 pt-1">
                  {step}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 sm:mt-12 text-center">
            <p className="text-gray-400 mb-6 sm:mb-8 text-base sm:text-lg">
              After installing, go back to the Add Funds page and refresh — Cash
              App will be ready!
            </p>

            <button
              onClick={() => navigate("/add-funds")}
              className="inline-flex items-center gap-2 bg-green-600/90 hover:bg-green-600 text-white px-7 sm:px-9 py-3.5 sm:py-4 rounded-2xl font-medium text-base sm:text-lg transition-colors shadow-lg shadow-green-900/30"
            >
              <CheckCircle size={20} />
              Return to Add Funds
            </button>
          </div>
        </div>

        {/* Footer note */}
        <div className="text-center text-gray-500 text-sm sm:text-base">
          <p className="mb-2">
            Desktop Google Chrome only • Required for peer-to-peer payment
            features
          </p>
          <p>Cashtap — Fast. Private. Decentralized.</p>
        </div>
      </div>
    </div>
  );
};

export default ExtentionInfo;
